// src/services/deliveryImport.ts
// -----------------------------------------------------------------------------
// Pure logic for the Unleashed "Delivery Address" CSV import (Phase 4 of the
// shelter-admin redesign — docs/workpaper-unleashed-import-shelters-users.md).
// Data in → plan out, no I/O: ImportPage's DeliveryAddressImport section feeds
// the parsed CSV + the portal's existing addresses in, gets back exactly what
// to create and what to skip, shows the plan, then executes it.
//
// Rules (John, 5 July 2026):
//   - Obsolete=TRUE rows are skipped (portal soft-delete model — we never
//     import dead addresses).
//   - Rows with no shelter code or no street are invalid.
//   - Rows for shelter codes the portal doesn't know are reported, not created.
//   - Dedupe: a row matching an EXISTING portal address (same shelter +
//     street + suburb + postcode, case/space-insensitive) is skipped — so
//     re-running the same file is a no-op (idempotent). In-file duplicates
//     collapse the same way.
//   - Suburb/City alignment (db-87): the DISPLAYED locality is `suburb_city`,
//     mapped from Unleashed Town/City (which post-cleanup holds the suburb),
//     falling back to the raw Suburb. The raw Unleashed `suburb` is retained for a
//     lossless round-trip. Dedup keys on the locality (city||suburb).
//   - Import data-quality signals: blankCity (rows with an empty Town/City) and
//     residualSuburb (rows whose Suburb is still populated) surface whether the
//     external Unleashed cleanup is finished.
//   - is_default: only when the shelter currently has NO addresses at all, its
//     FIRST created row becomes the default (existing defaults are never touched).

export interface DeliveryCsvRow {
  shelter_code: string;
  customer_name: string;
  address_name: string;
  street: string;
  street2: string;
  suburb: string;
  city: string;
  state: string;
  postcode: string;
  instruction: string;
  obsolete: boolean;
}

// Minimal RFC-4180-ish CSV parser: quoted fields, embedded commas/quotes/
// newlines, CRLF, BOM. Returns rows of raw string cells.
export function parseCsvCells(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const rows: string[][] = [];
  let cell = "";
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      i += 1;
      continue;
    }
    if (ch === "\r") {
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      cell = "";
      row = [];
      i += 1;
      continue;
    }
    cell += ch;
    i += 1;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

// Australian postcodes are always 4 digits; the ones that start with a leading zero
// (NT 08xx, ACT/NSW 02xx) lose it when the CSV is round-tripped through Excel, which
// reads "0812" as the number 812. Any purely-numeric postcode shorter than 4 digits is
// therefore missing leading zeros → left-pad to 4. Blank or non-numeric values pass
// through unchanged. Same defence as the supplier zero-pad (db-123).
export function padAuPostcode(pc: string | null | undefined): string {
  const t = (pc ?? "").trim();
  return /^\d{1,3}$/.test(t) ? t.padStart(4, "0") : t;
}

// Parse the Unleashed Delivery Address export into typed rows. Header-driven
// (column order doesn't matter); the leading "*" on "*Customer Code" is
// stripped like the existing shelter import does.
export function parseDeliveryCsv(text: string): { rows: DeliveryCsvRow[]; error: string | null } {
  const cells = parseCsvCells(text);
  if (cells.length < 2) return { rows: [], error: "No data rows found in the file." };
  const header = cells[0].map((h) => h.replace(/^\*/, "").trim());
  const idx = (name: string) => header.indexOf(name);
  if (idx("Customer Code") === -1 || idx("Delivery Street Address") === -1) {
    return {
      rows: [],
      error:
        'This doesn\'t look like the Unleashed Delivery Address export (expected "Customer Code" and "Delivery Street Address" columns).',
    };
  }
  const get = (r: string[], name: string) => {
    const j = idx(name);
    return j === -1 ? "" : (r[j] || "").trim();
  };
  const rows = cells.slice(1).map((r) => ({
    shelter_code: get(r, "Customer Code"),
    customer_name: get(r, "Customer Name"),
    address_name: get(r, "Delivery Address Name"),
    street: get(r, "Delivery Street Address"),
    street2: get(r, "Delivery Street Address 2"),
    suburb: get(r, "Delivery Suburb"),
    city: get(r, "Delivery Town/City"),
    state: get(r, "Delivery State/Region"),
    postcode: padAuPostcode(get(r, "Delivery Postal Code")),
    instruction: get(r, "Delivery Instruction"),
    obsolete: get(r, "Obsolete").toUpperCase() === "TRUE",
  }));
  return { rows, error: null };
}

export interface ExistingAddress {
  shelter_id?: string | null;
  street?: string | null;
  suburb?: string | null;
  suburb_city?: string | null;
  postcode?: string | null;
}

export interface PlannedAddress {
  shelter_id: string;
  address_name: string;
  street: string;
  street2: string | null;
  suburb: string | null;
  suburb_city: string | null;
  state: string | null;
  postcode: string | null;
  delivery_instruction: string | null;
  is_default: boolean;
  // db-146 promotion model: imported addresses are known-good → seed at the
  // promotion threshold (3) so they show on the shelter pick-list immediately.
  use_count: number;
}

// One flagged row, carried so the preview can SHOW which rows a data-quality
// count refers to. `csvLine` is the line in the uploaded file (header = line 1),
// so a flagged row can be found in Excel without hunting.
export interface DeliveryIssueRow {
  csvLine: number;
  shelterCode: string;
  addressName: string;
  street: string;
  suburb: string;
  city: string;
  state: string;
  postcode: string;
}

export interface DeliveryImportPlan {
  creates: PlannedAddress[];
  skippedExisting: number;
  obsoleteSkipped: number;
  invalidRows: number;
  unknownShelters: string[];
  defaultsAssigned: number;
  // Import data-quality signals over the importable rows (§4-B). blankCity: rows
  // with an empty Town/City (would import a blank locality). residualSuburb: rows
  // whose Suburb is still populated (Unleashed cleanup not finished).
  blankCity: number;
  residualSuburb: number;
  // The rows behind those counts. A count on its own tells you something needs
  // fixing but not WHICH row, which makes a 94-row cleanup in Unleashed guesswork.
  blankCityRows: DeliveryIssueRow[];
  residualSuburbRows: DeliveryIssueRow[];
  invalidRowDetails: DeliveryIssueRow[];
}

// Build a flagged-row record. `i` is the row's index within the parsed data rows;
// +2 converts it to the file's line number (1 header line, 1-based lines).
function issueRow(r: DeliveryCsvRow, i: number): DeliveryIssueRow {
  return {
    csvLine: i + 2,
    shelterCode: r.shelter_code,
    addressName: r.address_name,
    street: r.street,
    suburb: r.suburb,
    city: r.city,
    state: r.state,
    postcode: r.postcode,
  };
}

const norm = (v: string | null | undefined) => (v || "").toLowerCase().replace(/\s+/g, " ").trim();
const matchKey = (shelterId: string, street: string, locality: string, postcode: string) =>
  `${norm(shelterId)}|${norm(street)}|${norm(locality)}|${norm(padAuPostcode(postcode))}`;

export function planDeliveryImport(
  csvRows: DeliveryCsvRow[],
  shelterIdByCode: Record<string, string>,
  existing: ExistingAddress[]
): DeliveryImportPlan {
  // Resolve each CSV row's Customer Code to the portal-owned shelter_id (the key
  // delivery_addresses uses since db-83). Case/space-insensitive, like the address
  // dedupe, so a code differing only by case still matches a known shelter.
  const idByNormCode = new Map<string, string>();
  for (const code of Object.keys(shelterIdByCode)) {
    idByNormCode.set(norm(code), shelterIdByCode[code]);
  }
  const seen = new Set(
    existing.map((e) =>
      matchKey(
        e.shelter_id || "",
        e.street || "",
        e.suburb_city || e.suburb || "",
        e.postcode || ""
      )
    )
  );
  const sheltersWithAny = new Set(existing.map((e) => norm(e.shelter_id || "")));
  const plan: DeliveryImportPlan = {
    creates: [],
    skippedExisting: 0,
    obsoleteSkipped: 0,
    invalidRows: 0,
    unknownShelters: [],
    defaultsAssigned: 0,
    blankCity: 0,
    residualSuburb: 0,
    blankCityRows: [],
    residualSuburbRows: [],
    invalidRowDetails: [],
  };
  const unknown = new Set<string>();
  for (let i = 0; i < csvRows.length; i++) {
    const r = csvRows[i];
    if (r.obsolete) {
      plan.obsoleteSkipped += 1;
      continue;
    }
    if (!r.shelter_code || !r.street) {
      plan.invalidRowDetails.push(issueRow(r, i));
      continue;
    }
    const shelterId = idByNormCode.get(norm(r.shelter_code));
    if (!shelterId) {
      unknown.add(r.shelter_code);
      continue;
    }
    // The locality is Town/City (post-cleanup it holds the suburb), falling back
    // to the raw Suburb. Dedup keys on the locality.
    const locality = r.city || r.suburb;
    // Import data-quality signals (§4-B) over importable rows.
    if (!r.city) plan.blankCityRows.push(issueRow(r, i));
    if (r.suburb) plan.residualSuburbRows.push(issueRow(r, i));
    const key = matchKey(shelterId, r.street, locality, r.postcode);
    if (seen.has(key)) {
      plan.skippedExisting += 1;
      continue;
    }
    seen.add(key);
    const isDefault = !sheltersWithAny.has(norm(shelterId));
    if (isDefault) {
      sheltersWithAny.add(norm(shelterId));
      plan.defaultsAssigned += 1;
    }
    plan.creates.push({
      shelter_id: shelterId,
      address_name: r.address_name || "Delivery",
      street: r.street,
      street2: r.street2 || null,
      suburb: r.suburb || null,
      suburb_city: locality || null,
      state: r.state || null,
      postcode: padAuPostcode(r.postcode) || null,
      delivery_instruction: r.instruction || null,
      is_default: isDefault,
      use_count: 3, // db-146: known-good import → promoted immediately
    });
  }
  plan.unknownShelters = Array.from(unknown).sort();
  // Counts are derived from the lists, so the two can never disagree.
  plan.blankCity = plan.blankCityRows.length;
  plan.residualSuburb = plan.residualSuburbRows.length;
  plan.invalidRows = plan.invalidRowDetails.length;
  return plan;
}

// The "Default Delivery" address carried on a row of the STEP 1 Customers export.
// Returns the row to insert, or null when the shelter already has that address.
//
// It deliberately does NOT upsert on (shelter_id, address_name): db-129 DROPPED that
// unique index, so ON CONFLICT against it raises "no unique or exclusion constraint
// matching" — and because the caller discarded the error, the Customers import had
// been silently creating no default delivery address at all (finding A2, 18 Aug 2026).
//
// Dedupe is by CONTENT, on exactly the same key as the Step 2 delivery import, so the
// two importers agree about what "the same address" means. `existing` must include
// DEACTIVATED rows — same reason as Step 2: a re-import must never resurrect an
// address someone deliberately removed.
//
// is_default follows Step 2's rule (set only when the shelter has no addresses yet),
// so a re-import can't steal the default from an address chosen later by hand.
export function planDefaultDeliveryAddress(
  shelterId: string,
  row: {
    street: string;
    street2?: string | null;
    suburb?: string | null;
    city?: string | null;
    state?: string | null;
    postcode?: string | null;
    instruction?: string | null;
  },
  existing: ExistingAddress[]
): PlannedAddress | null {
  if (!shelterId || !row.street) return null;
  // Locality is Town/City, falling back to Suburb — the Step 2 convention.
  const locality = row.city || row.suburb || "";
  const postcode = padAuPostcode(row.postcode);
  const seen = new Set(
    existing.map((e) =>
      matchKey(
        e.shelter_id || "",
        e.street || "",
        e.suburb_city || e.suburb || "",
        e.postcode || ""
      )
    )
  );
  if (seen.has(matchKey(shelterId, row.street, locality, postcode))) return null;
  return {
    shelter_id: shelterId,
    address_name: "Default Delivery",
    street: row.street,
    street2: row.street2 || null,
    suburb: row.suburb || null,
    suburb_city: locality || null,
    state: row.state || null,
    postcode: postcode || null,
    delivery_instruction: row.instruction || null,
    is_default: existing.length === 0,
    use_count: 3, // db-146: known-good import → promoted immediately
  };
}
